import { slicedToArray as _slicedToArray, objectSpread2 as _objectSpread2, toConsumableArray as _toConsumableArray } from '../../../../_virtual/_rollupPluginBabelHelpers.js';
import { useLayoutEffect, useEffect, useRef, useMemo, useCallback } from 'react';

function useCombinedRefs() {
  for (var _len = arguments.length, refs = new Array(_len), _key = 0; _key < _len; _key++) {
    refs[_key] = arguments[_key];
  }
  return useMemo(function () {
    return function (node) {
      refs.forEach(function (ref) {
        return ref(node);
      });
    };
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  refs);
}

// https://github.com/facebook/react/blob/master/packages/shared/ExecutionEnvironment.js
var canUseDOM = typeof window !== 'undefined' && typeof window.document !== 'undefined' && typeof window.document.createElement !== 'undefined';
function isWindow(element) {
  var elementString = Object.prototype.toString.call(element);
  return elementString === '[object Window]' ||
  // In Electron context the Window object serializes to [object global]
  elementString === '[object global]';
}
function isNode(node) {
  return 'nodeType' in node;
}
function getWindow(target) {
  var _target$ownerDocument, _target$ownerDocument2;
  if (!target) {
    return window;
  }
  if (isWindow(target)) {
    return target;
  }
  if (!isNode(target)) {
    return window;
  }
  return (_target$ownerDocument = (_target$ownerDocument2 = target.ownerDocument) == null ? void 0 : _target$ownerDocument2.defaultView) != null ? _target$ownerDocument : window;
}
function isDocument(node) {
  var _getWindow = getWindow(node),
    Document = _getWindow.Document;
  return node instanceof Document;
}
function isHTMLElement(node) {
  if (isWindow(node)) {
    return false;
  }
  return node instanceof getWindow(node).HTMLElement;
}
function isSVGElement(node) {
  return node instanceof getWindow(node).SVGElement;
}
function getOwnerDocument(target) {
  if (!target) {
    return document;
  }
  if (isWindow(target)) {
    return target.document;
  }
  if (!isNode(target)) {
    return document;
  }
  if (isDocument(target)) {
    return target;
  }
  if (isHTMLElement(target) || isSVGElement(target)) {
    return target.ownerDocument;
  }
  return document;
}

/**
 * A hook that resolves to useEffect on the server and useLayoutEffect on the client
 * @param callback {function} Callback function that is invoked when the dependencies of the hook change
 */

var useIsomorphicLayoutEffect = canUseDOM ? useLayoutEffect : useEffect;
function useEvent(handler) {
  var handlerRef = useRef(handler);
  useIsomorphicLayoutEffect(function () {
    handlerRef.current = handler;
  });
  return useCallback(function () {
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }
    return handlerRef.current == null ? void 0 : handlerRef.current.apply(handlerRef, args);
  }, []);
}
function useInterval() {
  var intervalRef = useRef(null);
  var set = useCallback(function (listener, duration) {
    intervalRef.current = setInterval(listener, duration);
  }, []);
  var clear = useCallback(function () {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);
  return [set, clear];
}
function useLatestValue(value, dependencies) {
  if (dependencies === void 0) {
    dependencies = [value];
  }
  var valueRef = useRef(value);
  useIsomorphicLayoutEffect(function () {
    if (valueRef.current !== value) {
      valueRef.current = value;
    }
  }, dependencies);
  return valueRef;
}
function useLazyMemo(callback, dependencies) {
  var valueRef = useRef();
  return useMemo(function () {
    var newValue = callback(valueRef.current);
    valueRef.current = newValue;
    return newValue;
  }, // eslint-disable-next-line react-hooks/exhaustive-deps
  _toConsumableArray(dependencies));
}
function useNodeRef(onChange) {
  var onChangeHandler = useEvent(onChange);
  var node = useRef(null);
  var setNodeRef = useCallback(function (element) {
    if (element !== node.current) {
      onChangeHandler == null ? void 0 : onChangeHandler(element, node.current);
    }
    node.current = element;
  },
  //eslint-disable-next-line
  []);
  return [node, setNodeRef];
}
function usePrevious(value) {
  var ref = useRef();
  useEffect(function () {
    ref.current = value;
  }, [value]);
  return ref.current;
}
var ids = {};
function useUniqueId(prefix, value) {
  return useMemo(function () {
    if (value) {
      return value;
    }
    var id = ids[prefix] == null ? 0 : ids[prefix] + 1;
    ids[prefix] = id;
    return prefix + "-" + id;
  }, [prefix, value]);
}
function createAdjustmentFn(modifier) {
  return function (object) {
    for (var _len = arguments.length, adjustments = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
      adjustments[_key - 1] = arguments[_key];
    }
    return adjustments.reduce(function (accumulator, adjustment) {
      var entries = Object.entries(adjustment);
      for (var _i = 0, _entries = entries; _i < _entries.length; _i++) {
        var _entries$_i = _slicedToArray(_entries[_i], 2),
          key = _entries$_i[0],
          valueAdjustment = _entries$_i[1];
        var value = accumulator[key];
        if (value != null) {
          accumulator[key] = value + modifier * valueAdjustment;
        }
      }
      return accumulator;
    }, _objectSpread2({}, object));
  };
}
var add = /*#__PURE__*/createAdjustmentFn(1);
var subtract = /*#__PURE__*/createAdjustmentFn(-1);
function hasViewportRelativeCoordinates(event) {
  return 'clientX' in event && 'clientY' in event;
}
function isKeyboardEvent(event) {
  if (!event) {
    return false;
  }
  var _getWindow2 = getWindow(event.target),
    KeyboardEvent = _getWindow2.KeyboardEvent;
  return KeyboardEvent && event instanceof KeyboardEvent;
}
function isTouchEvent(event) {
  if (!event) {
    return false;
  }
  var _getWindow3 = getWindow(event.target),
    TouchEvent = _getWindow3.TouchEvent;
  return TouchEvent && event instanceof TouchEvent;
}

/**
 * Returns the normalized x and y coordinates for mouse and touch events.
 */

function getEventCoordinates(event) {
  if (isTouchEvent(event)) {
    if (event.touches && event.touches.length) {
      var _event$touches$ = event.touches[0],
        x = _event$touches$.clientX,
        y = _event$touches$.clientY;
      return {
        x: x,
        y: y
      };
    } else if (event.changedTouches && event.changedTouches.length) {
      var _event$changedTouches = event.changedTouches[0],
        _x = _event$changedTouches.clientX,
        _y = _event$changedTouches.clientY;
      return {
        x: _x,
        y: _y
      };
    }
  }
  if (hasViewportRelativeCoordinates(event)) {
    return {
      x: event.clientX,
      y: event.clientY
    };
  }
  return null;
}
var CSS = /*#__PURE__*/Object.freeze({
  Translate: {
    toString: function toString(transform) {
      if (!transform) {
        return;
      }
      var x = transform.x,
        y = transform.y;
      return "translate3d(" + (x ? Math.round(x) : 0) + "px, " + (y ? Math.round(y) : 0) + "px, 0)";
    }
  },
  Scale: {
    toString: function toString(transform) {
      if (!transform) {
        return;
      }
      var scaleX = transform.scaleX,
        scaleY = transform.scaleY;
      return "scaleX(" + scaleX + ") scaleY(" + scaleY + ")";
    }
  },
  Transform: {
    toString: function toString(transform) {
      if (!transform) {
        return;
      }
      return [CSS.Translate.toString(transform), CSS.Scale.toString(transform)].join(' ');
    }
  },
  Transition: {
    toString: function toString(_ref) {
      var property = _ref.property,
        duration = _ref.duration,
        easing = _ref.easing;
      return property + " " + duration + "ms " + easing;
    }
  }
});
var SELECTOR = 'a,frame,iframe,input:not([type=hidden]):not(:disabled),select:not(:disabled),textarea:not(:disabled),button:not(:disabled),*[tabindex]';
function findFirstFocusableNode(element) {
  if (element.matches(SELECTOR)) {
    return element;
  }
  return element.querySelector(SELECTOR);
}

export { CSS, add, canUseDOM, findFirstFocusableNode, getEventCoordinates, getOwnerDocument, getWindow, hasViewportRelativeCoordinates, isDocument, isHTMLElement, isKeyboardEvent, isNode, isSVGElement, isTouchEvent, isWindow, subtract, useCombinedRefs, useEvent, useInterval, useIsomorphicLayoutEffect, useLatestValue, useLazyMemo, useNodeRef, usePrevious, useUniqueId };
